/**
 * 会话详情抽屉组件
 *
 * 显示会话基本信息和消息记录
 */

import React, { useState, useEffect } from 'react';
import { Drawer, Descriptions, Tag, Spin, Empty, Divider, Button, message } from 'antd';
import { UserOutlined, RobotOutlined, CopyOutlined } from '@ant-design/icons';
import { conversationApi } from '@/services/api';
import { formatDateTime, formatTime } from '@/utils/date';
import type { AdminConversationDetail, AdminMessageItem } from '@/services/types';

// 状态标签颜色映射
const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
    active: { bg: '#DCFCE7', text: '#16A34A', label: '活跃' },
    closed: { bg: '#F3F4F6', text: '#6B7280', label: '已关闭' },
    archived: { bg: '#FEF3C7', text: '#D97706', label: '已归档' },
};

// 状态标签组件
const StatusTag: React.FC<{ status: string }> = ({ status }) => {
    const config = STATUS_COLORS[status] || STATUS_COLORS.active;
    return (
        <Tag
            style={{
                backgroundColor: config.bg,
                color: config.text,
                border: 'none',
                borderRadius: 12,
                padding: '2px 12px',
            }}
        >
            {config.label}
        </Tag>
    );
};


// 格式化消息为文本（用于复制）
export const formatMessagesAsText = (data: AdminConversationDetail): string => {
    const lines: string[] = [];

    // 添加会话信息
    lines.push(`会话ID: ${data.session_id}`);
    lines.push(`用户: ${data.username || '匿名用户'}`);
    lines.push(`开始时间: ${formatDateTime(data.started_at)}`);
    lines.push(`消息数: ${data.message_count}`);
    lines.push('');

    // 添加消息内容
    data.messages.forEach((msg) => {
        const role = msg.role === 'user' ? '用户' : 'AI';
        const time = formatTime(msg.created_at);
        lines.push(`${role} [${time}]`);
        lines.push(msg.content);
        lines.push('');
    });

    return lines.join('\n');
};

// 消息气泡组件
const MessageBubble: React.FC<{ message: AdminMessageItem }> = ({ message }) => {
    const isUser = message.role === 'user';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
            {/* 角色和时间 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isUser ? (
                    <UserOutlined style={{ color: '#2563EB', fontSize: 14 }} />
                ) : (
                    <RobotOutlined style={{ color: '#16A34A', fontSize: 14 }} />
                )}
                <span
                    style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: isUser ? '#2563EB' : '#16A34A',
                    }}
                >
                    {isUser ? '用户' : '助手'}
                </span>
                <span style={{ fontSize: 11, color: '#A1A1AA' }}>
                    {formatTime(message.created_at)}
                </span>
            </div>

            {/* 消息内容 */}
            <div
                style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    backgroundColor: isUser ? '#EFF6FF' : '#F0FDF4',
                    maxWidth: '100%',
                }}
            >
                <p
                    style={{
                        margin: 0,
                        fontSize: 13,
                        color: '#18181B',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}
                >
                    {message.content}
                </p>
            </div>

            {/* 意图标签（如果有） */}
            {message.intent && (
                <div style={{ marginTop: 4 }}>
                    <Tag
                        style={{
                            fontSize: 11,
                            backgroundColor: '#F3F4F6',
                            border: 'none',
                        }}
                    >
                        意图: {message.intent}
                        {message.confidence && ` (${(message.confidence * 100).toFixed(0)}%)`}
                    </Tag>
                </div>
            )}
        </div>
    );
};

interface Props {
    sessionId: string | null;
    open: boolean;
    onClose: () => void;
}

const ConversationDetailDrawer: React.FC<Props> = ({ sessionId, open, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<AdminConversationDetail | null>(null);
    const [copying, setCopying] = useState(false);

    // 加载详情数据
    useEffect(() => {
        if (open && sessionId) {
            setLoading(true);
            conversationApi
                .get(sessionId)
                .then(setData)
                .catch((error) => {
                    console.error('加载会话详情失败:', error);
                    setData(null);
                })
                .finally(() => setLoading(false));
        } else {
            // 关闭时清空数据
            setData(null);
        }
    }, [open, sessionId]);

    // 处理复制全部
    const handleCopyAll = async () => {
        if (!data) return;

        setCopying(true);
        try {
            const text = formatMessagesAsText(data);
            await navigator.clipboard.writeText(text);
            message.success('已复制到剪切板');
        } catch (error) {
            console.error('复制失败:', error);
            message.error('复制失败，请重试');
        } finally {
            setCopying(false);
        }
    };

    return (
        <Drawer
            title="会话详情"
            placement="right"
            width={480}
            open={open}
            onClose={onClose}
            destroyOnClose
        >
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                    <Spin size="large" />
                </div>
            ) : !data ? (
                <Empty description="未找到会话数据" />
            ) : (
                <>
                    {/* 基本信息 */}
                    <Descriptions column={1} size="small" labelStyle={{ width: 80 }}>
                        <Descriptions.Item label="会话ID">
                            <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                                {data.session_id}
                            </span>
                        </Descriptions.Item>
                        <Descriptions.Item label="用户">
                            {data.username || (
                                <span style={{ color: '#A1A1AA' }}>匿名用户</span>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="状态">
                            <StatusTag status={data.status} />
                        </Descriptions.Item>
                        <Descriptions.Item label="渠道">{data.channel}</Descriptions.Item>
                        <Descriptions.Item label="开始时间">
                            {formatDateTime(data.started_at)}
                        </Descriptions.Item>
                        <Descriptions.Item label="消息数">
                            {data.message_count} 条
                        </Descriptions.Item>
                    </Descriptions>

                    <Divider style={{ margin: '20px 0' }} />

                    {/* 消息记录 */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12
                    }}>
                        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>
                            消息记录
                        </h3>
                        <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={handleCopyAll}
                            loading={copying}
                            size="small"
                            style={{ color: '#1677ff', height: 'auto', padding: '4px 8px' }}
                        >
                            复制消息
                        </Button>
                    </div>
                    <div
                        style={{
                            maxHeight: 500,
                            overflowY: 'auto',
                            paddingRight: 8,
                        }}
                    >
                        {data.messages.length === 0 ? (
                            <Empty description="暂无消息记录" />
                        ) : (
                            data.messages.map((msg) => (
                                <MessageBubble key={msg.id} message={msg} />
                            ))
                        )}
                    </div>
                </>
            )}
        </Drawer >
    );
};

export default ConversationDetailDrawer;
